<script>

    import { expandAndScrollToElement, util } from '@/Helpers'

    /**
     * Scrolls to a nav item's section. Items with `expand` open their collapsible section before scrolling to it.
     */
    export function scrollToNavItem(item) {
        if (item.expand) {
            expandAndScrollToElement(item.id)
        } else {
            util.scrollToElement(item.id)
        }
    }

    /**
     * Whether a route hash names the nav item's section (`#notes`) or, for items with `entryHash`, an entry inside it
     * (`#notes-<id>`, such as a Notes & Activity timeline permalink).
     */
    export function navItemMatchesHash(item, hash) {
        const sectionHash = '#' + item.id
        if (hash === sectionHash) {
            return true
        }

        return Boolean(item.entryHash) && typeof hash === 'string' && hash.startsWith(sectionHash + '-') && /^\d+$/.test(hash.slice(sectionHash.length + 1))
    }

    /**
     * Section-level deep link: scrolls to the nav item whose section a route hash such as `#notes` or `#notes-<id>` names.
     * Returns false when no item matches.
     */
    export function scrollToNavItemFromHash(hash, items) {
        const item = (items || []).find((navItem) => navItemMatchesHash(navItem, hash))
        if (!item) {
            return false
        }
        scrollToNavItem(item)

        return true
    }
</script>

<script setup>

    import { useViewStore } from '@/Stores'
    import { storeToRefs } from 'pinia'

    import { ExclamationTriangleIcon } from '@heroicons/vue/16/solid'

    const viewStore = useViewStore()

    const { scrollDist, pageNavTitle, pageNavItems } = storeToRefs(viewStore)

    function handleNavItemClick(item) {
        scrollToNavItem(item)
        viewStore.clickNavItem(item)
    }
</script>

<template>

    <div
        v-if="pageNavItems && Object.keys(pageNavItems).length > 0"
        class="fixed z-floating-nav left-5 transition-all duration-300 flex"
        :class="{
            'bottom-5': scrollDist > 500,
            '-bottom-32 lg:bottom-5': scrollDist <= 500,
        }"
    >

        <div
            class="hidden xl:flex"
        >

            <ol>
                <li
                    v-if="pageNavTitle"
                    class="text-lifeworx-blue-700 hover:hover:text-lifeworx-blue-800 text-sm font-bold mb-4 cursor-pointer"
                    @click="() => handleNavItemClick({ label: null, id: 'app' })"
                >
                    {{ pageNavTitle }}
                </li>
                <template
                    v-for="item in pageNavItems"
                >
                    <li
                        class="flex items-center gap-1.5 text-xs text-lifeworx-blue-600 font-bold mb-2 cursor-pointer group"
                        @click="() => handleNavItemClick(item)"
                        :class="{
                            'text-lifeworx-red-500 hover:text-lifeworx-red-700': item.status === 'alert',
                            'text-lifeworx-orange-500 hover:text-lifeworx-orange-700': item.status === 'warn',
                            'hover:text-lifeworx-blue-700': typeof item.status === 'number' || !item.status
                        }"
                    >
                        {{ item.label }}
                        <span
                            v-if="item.status && item.status !== 'alert' && item.status !== 'warn'"
                            class="inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-semibold"
                            :class="{
                                'bg-lifeworx-blue-600 text-white group-hover:bg-lifeworx-blue-700': typeof item.status === 'number'
                            }"
                        >
                            {{ typeof item.status === 'number' ? `${item.status}%` : item.status }}
                        </span>
                        <ExclamationTriangleIcon
                            v-if="item.status === 'alert' || item.status === 'warn'"
                            class="size-4 inline-flex"
                        />
                    </li>
                </template>
            </ol>

        </div>

    </div>

</template>
